(function() {
  'use strict';

  const CartDrawer = {
    selectors: {
      drawer: '[data-cart-drawer]',
      overlay: '[data-cart-drawer-overlay]',
      close: '[data-cart-drawer-close]',
      content: '[data-cart-drawer-content]',
      form: '[data-cart-form]',
      quantityInput: '[data-quantity-input]',
      quantityIncrease: '[data-quantity-increase]',
      quantityDecrease: '[data-quantity-decrease]',
      remove: '[data-cart-remove]',
      error: '[data-cart-error-message-wrapper]',
      errorMessage: '[data-cart-error-message]',
      cartCount: '[data-cart-count-bubble]',
      cartCountNumber: '[data-cart-count]'
    },

    init: function() {
      this.drawer = document.querySelector(this.selectors.drawer);
      if (!this.drawer) return;

      this.bindEvents();
      this.updateCartCount();
    },

    bindEvents: function() {
      // Close drawer events
      const closeBtn = this.drawer.querySelector(this.selectors.close);
      const overlay = this.drawer.querySelector(this.selectors.overlay);
      
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }
      
      if (overlay) {
        overlay.addEventListener('click', () => this.close());
      }

      // Quantity controls
      const quantityInputs = this.drawer.querySelectorAll(this.selectors.quantityInput);
      quantityInputs.forEach(input => {
        // Store original quantity
        input.setAttribute('data-original-quantity', input.value);
        
        input.addEventListener('change', (e) => this.updateQuantity(e));
        input.addEventListener('blur', (e) => {
          // Only update if value actually changed
          const currentValue = parseInt(e.target.value, 10);
          const originalValue = parseInt(e.target.getAttribute('data-original-quantity'), 10);
          if (currentValue !== originalValue) {
            this.updateQuantity(e);
          }
        });
      });

      const increaseBtns = this.drawer.querySelectorAll(this.selectors.quantityIncrease);
      increaseBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          const line = btn.getAttribute('data-line');
          this.changeQuantity(line, 1);
        });
      });

      const decreaseBtns = this.drawer.querySelectorAll(this.selectors.quantityDecrease);
      decreaseBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          const line = btn.getAttribute('data-line');
          this.changeQuantity(line, -1);
        });
      });

      // Remove items
      const removeBtns = this.drawer.querySelectorAll(this.selectors.remove);
      removeBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const line = btn.getAttribute('data-line');
          this.removeItem(line);
        });
      });

      // Form submission
      const form = this.drawer.querySelector(this.selectors.form);
      if (form) {
        form.addEventListener('submit', (e) => {
          // Let checkout proceed normally
          // Update button will be handled by updateQuantity
        });
      }

      // ESC key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    },

    open: function() {
      if (!this.drawer) return;
      
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      
      // Focus management
      const closeBtn = this.drawer.querySelector(this.selectors.close);
      if (closeBtn) {
        setTimeout(() => closeBtn.focus(), 100);
      }
    },

    close: function() {
      if (!this.drawer) return;
      
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
    },

    isOpen: function() {
      return this.drawer && this.drawer.getAttribute('aria-hidden') === 'false';
    },

    updateQuantity: function(event) {
      const input = event.target;
      const line = input.getAttribute('data-line');
      const quantity = parseInt(input.value, 10) || 0;
      
      this.changeQuantity(line, null, quantity);
    },

    changeQuantity: function(line, delta, quantity) {
      const input = this.drawer.querySelector(`[data-line="${line}"][data-quantity-input]`);
      if (!input) return;
      
      const currentQuantity = quantity !== null ? quantity : 
        (parseInt(input.value || 0, 10) + delta);
      
      if (currentQuantity < 0) {
        input.value = 0;
        return;
      }

      this.showLoading();
      
      fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: currentQuantity
        })
      })
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(cart => {
        this.updateCart(cart);
        this.hideError();
      })
      .catch(error => {
        console.error('Error updating cart:', error);
        this.showError('Unable to update cart. Please try again.');
        // Restore original value on error
        if (input) {
          input.value = parseInt(input.getAttribute('data-original-quantity') || input.value, 10);
        }
      })
      .finally(() => {
        this.hideLoading();
      });
    },

    removeItem: function(line) {
      this.showLoading();
      
      fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: 0
        })
      })
      .then(response => response.json())
      .then(cart => {
        this.updateCart(cart);
        this.hideError();
      })
      .catch(error => {
        console.error('Error removing item:', error);
        this.showError('Unable to remove item. Please try again.');
      })
      .finally(() => {
        this.hideLoading();
      });
    },

    updateCart: function(cart) {
      // Update cart count
      this.updateCartCount(cart.item_count);
      
      // If cart is empty, show empty state
      if (cart.item_count === 0) {
        this.showEmptyCart();
        return;
      }

      // Reload the drawer content
      this.reloadDrawer();
    },

    reloadDrawer: function() {
      // Get section ID from the drawer
      const sectionId = this.drawer?.closest('[data-section-id]')?.getAttribute('data-section-id');
      if (!sectionId) return;

      const url = `${window.Shopify.routes.root}?section_id=${sectionId}`;
      
      fetch(url)
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newDrawer = doc.querySelector('[data-cart-drawer]');
          
          if (newDrawer && this.drawer) {
            const content = this.drawer.querySelector(this.selectors.content);
            const newContent = newDrawer.querySelector(this.selectors.content);
            
            if (content && newContent) {
              content.innerHTML = newContent.innerHTML;
              this.bindEvents(); // Rebind events for new content
            }
          }
        })
        .catch(error => {
          console.error('Error reloading cart drawer:', error);
          // Fallback: reload the page if section reload fails
          window.location.reload();
        });
    },

    showEmptyCart: function() {
      const wrapper = this.drawer.querySelector('[data-cart-wrapper]');
      const empty = this.drawer.querySelector('[data-empty-cart]');
      
      if (wrapper) wrapper.classList.add('hide');
      if (empty) empty.classList.remove('hide');
    },

    updateCartCount: function(count) {
      if (count === undefined) {
        // Fetch current cart count
        fetch(`${window.Shopify.routes.root}cart.js`)
          .then(response => response.json())
          .then(cart => {
            this.updateCartCount(cart.item_count);
          })
          .catch(() => {});
        return;
      }

      const cartCountBubble = document.querySelector(this.selectors.cartCount);
      const cartCountNumber = document.querySelector(this.selectors.cartCountNumber);
      
      if (cartCountBubble) {
        if (count > 0) {
          cartCountBubble.classList.remove('hide');
        } else {
          cartCountBubble.classList.add('hide');
        }
      }
      
      if (cartCountNumber) {
        cartCountNumber.textContent = count;
      }
    },

    showLoading: function() {
      // Add loading state if needed
      this.drawer?.classList.add('is-loading');
    },

    hideLoading: function() {
      this.drawer?.classList.remove('is-loading');
    },

    showError: function(message) {
      const errorWrapper = this.drawer.querySelector(this.selectors.error);
      const errorMessage = this.drawer.querySelector(this.selectors.errorMessage);
      
      if (errorWrapper) {
        errorWrapper.classList.remove('hide');
      }
      
      if (errorMessage) {
        errorMessage.textContent = message;
      }
    },

    hideError: function() {
      const errorWrapper = this.drawer.querySelector(this.selectors.error);
      if (errorWrapper) {
        errorWrapper.classList.add('hide');
      }
    }
  };

  // Global function to open cart drawer
  window.openCartDrawer = function() {
    CartDrawer.open();
  };

  // Handle cart drawer trigger buttons
  document.addEventListener('DOMContentLoaded', function() {
    const triggers = document.querySelectorAll('[data-cart-drawer-trigger]');
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        CartDrawer.open();
      });
    });
  });

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartDrawer.init());
  } else {
    CartDrawer.init();
  }

  // Listen for cart updates from other parts of the site
  document.addEventListener('cart:updated', (e) => {
    if (e.detail && e.detail.item_count !== undefined) {
      const previousCount = parseInt(CartDrawer.drawer?.querySelector('[data-cart-count]')?.textContent || '0', 10);
      const newCount = e.detail.item_count;
      
      CartDrawer.updateCartCount(newCount);
      
      if (!CartDrawer.drawer) return; // Drawer not enabled
      
      if (CartDrawer.isOpen()) {
        CartDrawer.reloadDrawer();
      } else if (newCount > previousCount) {
        // Item was added - open drawer automatically
        CartDrawer.open();
        setTimeout(() => CartDrawer.reloadDrawer(), 100);
      }
    }
  });

  // Watch for cart count changes (for add to cart from product pages)
  // Only if drawer is enabled
  if (CartDrawer.drawer) {
    let previousCartCount = parseInt(document.querySelector('[data-cart-count]')?.textContent || '0', 10);
    const cartCountObserver = new MutationObserver(function(mutations) {
      const currentCount = parseInt(document.querySelector('[data-cart-count]')?.textContent || '0', 10);
      if (currentCount > previousCartCount && !CartDrawer.isOpen() && CartDrawer.drawer) {
        // Cart count increased - item was added
        CartDrawer.open();
        setTimeout(() => CartDrawer.reloadDrawer(), 100);
      }
      previousCartCount = currentCount;
    });

    const cartCountElement = document.querySelector('[data-cart-count]');
    if (cartCountElement && cartCountElement.nodeType === Node.ELEMENT_NODE) {
      try {
        cartCountObserver.observe(cartCountElement, {
          childList: true,
          subtree: true,
          characterData: true
        });
      } catch (error) {
        console.warn('Cart drawer: Failed to observe cart count element', error);
      }
    }
  }
})();

